"use client"

import { useState, useEffect, useMemo } from "react"
import { CloseIcon } from "@/components/icons/pixel-icons"
import { itemDefinitions, getPhaseTerminalItems } from "@/data/item-types"
import { ItemCard } from "@/components/ui/item-card"
import type { ItemDefinition, HeroInventory } from "@/types/item.types"
import { playBuySound } from "@/hooks/use-sound-effects"
import { getCloneChargeCostByLevel } from "@/systems/item-system"

interface PhaseTerminalWindowProps {
  isOpen: boolean
  money: number
  heroLevel: number
  inventory: HeroInventory
  onClose: () => void
  onBuyItem: (itemDefId: string, price: number) => void
  onBuyCloneCharge: (cost: number) => void
}

const PRICE_MARKUP = 2.0 // Twice market prices

export const PhaseTerminalWindow = ({
  isOpen,
  money,
  heroLevel,
  inventory,
  onClose,
  onBuyItem,
  onBuyCloneCharge,
}: PhaseTerminalWindowProps) => {
  // Calculate clone beacon price based on hero level
  const cloneBeaconPrice = useMemo(() => getCloneChargeCostByLevel(heroLevel), [heroLevel])
  // Generate 3 random items when the terminal opens
  const [shopItems, setShopItems] = useState<ItemDefinition[]>([])
  // Track which items have been sold
  const [soldItems, setSoldItems] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (isOpen) {
      // Get 3 items for phase terminal (always includes clone activation beacon)
      const items = getPhaseTerminalItems(3)
      setShopItems(items)
      // Reset sold items when terminal opens fresh
      setSoldItems(new Set())
    }
  }, [isOpen])

  // Check if inventory has space
  const hasInventorySpace = useMemo(() => {
    return inventory.slots.some(slot => slot === null)
  }, [inventory])

  // Check if can stack with existing item
  const canStackItem = (itemDefId: string): boolean => {
    const itemDef = itemDefinitions[itemDefId]
    if (!itemDef || !itemDef.stackable) return false

    return inventory.slots.some(
      slot => slot && slot.itemDefId === itemDefId && slot.quantity < itemDef.maxStack
    )
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Window */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
        <div
          className="bg-gradient-to-b from-blue-950/95 to-slate-950/95 border-2 border-blue-500/50 rounded-xl shadow-2xl shadow-blue-500/20 max-w-2xl w-full pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
        >
          {/* Header */}
          <div className="relative p-4 border-b border-blue-500/30">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-blue-300 hover:text-white transition p-1"
            >
              <CloseIcon size={24} />
            </button>

            <div className="text-center">
              <h2 className="text-2xl font-bold text-blue-200 mb-1 flex items-center justify-center gap-2">
                <span className="text-3xl">⟁</span>
                Phase Terminal
                <span className="text-3xl">⟁</span>
              </h2>
              <p className="text-blue-300/70 text-xs mt-1">
                Prices are twice as high due to dimensional instability
              </p>
            </div>

            {/* Player Gold */}
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/30 rounded-lg px-3 py-1.5">
              <img
                src="/images/icon-coins.png"
                alt="gold"
                className="w-5 h-5"
                style={{ imageRendering: "pixelated" }}
              />
              <span className="text-game-gold font-bold">{money}</span>
            </div>
          </div>

          {/* Items Grid */}
          <div className="p-4">
            <div className="grid grid-cols-3 gap-4">
              {shopItems.map((item) => {
                const isSold = soldItems.has(item.id)
                // Clone beacon uses charge-based pricing, other items use 2x markup
                const isCloneBeacon = item.id === "clone-activation-beacon"
                const phasePrice = isCloneBeacon
                  ? cloneBeaconPrice
                  : Math.ceil(item.cost * PRICE_MARKUP)
                const canAfford = money >= phasePrice
                // Clone beacon always "can pickup" since it just adds charges to existing beacon
                const canPickup = isCloneBeacon || hasInventorySpace || canStackItem(item.id)

                // Clone beacon is never marked as sold - it remains available with increasing price
                if (isSold && !isCloneBeacon) {
                  // Show sold out state
                  return (
                    <div
                      key={item.id}
                      className="relative bg-slate-900/80 border-2 border-slate-700/50 rounded-lg p-3 flex flex-col items-center justify-center min-h-40"
                    >
                      <div className="text-slate-500 text-4xl mb-2">✗</div>
                      <div className="text-slate-500 font-bold text-sm">SOLD OUT</div>
                    </div>
                  )
                }

                return (
                  <ItemCard
                    key={item.id}
                    itemDef={item}
                    price={phasePrice}
                    canAfford={canAfford}
                    canPickup={canPickup}
                    onBuy={() => {
                      playBuySound()
                      if (isCloneBeacon) {
                        // Clone beacon: add charge to existing beacon, don't add to inventory
                        onBuyCloneCharge(phasePrice)
                      } else {
                        onBuyItem(item.id, phasePrice)
                        setSoldItems(prev => new Set(prev).add(item.id))
                      }
                    }}
                  />
                )
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-blue-500/30 text-center">
            <p className="text-blue-400/60 text-xs">
              The droppod's phase drive allows limited interdimensional trade
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
